import { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { logoutUser, oidcLogout } from '../../slices/thunks';
import axios from 'axios';
import { api } from '../../config';
import { getAuthToken } from '../../auth/tokenvalidation';
import { SESSION_ENDED_KEY } from '../../helpers/sessionManager';
import { clearLocalStorageKeepDevice } from '../../helpers/deviceId';

const broadcastLogout = () =>
  localStorage.setItem(
    SESSION_ENDED_KEY,
    JSON.stringify({ code: 'USER_LOGOUT', at: Date.now() })
  );

// Logout proses:
// - OIDC : BE logout (best-effort) → oidcLogout() TERAKHIR yang melakukan
//   signoutRedirect() ke IdP. Tanpa sessionStorage/localStorage.clear() dan
//   tanpa window.location.href — keduanya akan menimpa/melomba-i navigasi ke
//   IdP sekaligus menghapus signout-state + user oidc-client-ts, sehingga
//   RP-initiated logout tidak pernah selesai (sesi SSO di IdP tetap hidup).
// - Non-OIDC : local logout → BE logout → clear storage → redirect /login.
//
// Guard useRef: effect hanya jalan SATU kali. Tanpa ini, dispatch
// logoutOidcSuccess mengubah hasOidc (true→false) dan token hilang setelah
// purge → deps berubah → effect jalan lagi → jalur non-OIDC (clear + href
// /login) menimpa signoutRedirect run pertama.
const Logout = () => {
  const dispatch = useDispatch();
  const ranOnce = useRef(false);
  const token = getAuthToken();
  const { hasOidc } = useSelector(state => state.Login);
  const isOidcUser = hasOidc;

  useEffect(() => {
    if (ranOnce.current) return;
    ranOnce.current = true;

    const executeLogout = async () => {
      if (isOidcUser) {
        try {
          // 1. BE logout dulu (best-effort) — sebelum page unload ke IdP.
          await Promise.race([
            axios.post(`${api.API_URL}/account/logout`, null, {
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
            }),
            new Promise((_, reject) =>
              setTimeout(() => reject(new Error('Timeout')), 3000)
            ),
          ]);
        } catch (apiError) {
          console.warn('Logout API warning:', apiError.message);
        }

        try {
          // Tab lain ikut keluar tanpa pesan (event 'storage' di SessionMonitor).
          broadcastLogout();
          // 2. Langkah TERAKHIR: reset redux + purge + signoutRedirect ke IdP.
          //    Tidak ada clear storage / redirect lokal setelah ini.
          await dispatch(oidcLogout());
        } catch (error) {
          console.error('OIDC logout error:', error);
          // Fallback bila signoutRedirect gagal (mis. user tak ada di store):
          // tetap paksa clear lokal + kembali ke /login supaya user tidak
          // tersangkut di halaman kosong.
          sessionStorage.clear();
          clearLocalStorageKeepDevice();
          broadcastLogout();
          window.location.href = '/login';
        }
        return;
      }

      // Non-OIDC: local logout → BE logout → clear → redirect.
      try {
        await dispatch(logoutUser());
        try {
          await Promise.race([
            axios.post(`${api.API_URL}/account/logout`, null, {
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
            }),
            new Promise((_, reject) =>
              setTimeout(() => reject(new Error('Timeout')), 3000)
            ),
          ]);
        } catch (apiError) {
          console.warn('Logout API warning:', apiError.message);
        }
      } catch (error) {
        console.error('Logout process error:', error);
      } finally {
        sessionStorage.clear();
        clearLocalStorageKeepDevice();
        broadcastLogout();
        window.location.href = '/login';
      }
    };

    executeLogout();
    // isOidcToken/token sengaja tidak dimasukkan ke deps: snapshot diambil
    // saat mount pertama; ranOnce menjaga agar tidak jalan dua kali.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  return null;
};

export default Logout;
